import React, { useState, useEffect } from 'react';
import { X, CreditCard, AlertCircle, Check, Loader, CircleDot, Circle, Sparkles, MessageSquare } from 'lucide-react';
import { openExternalUrl } from '../utils/navigation';
import { EventContextHeader } from './EventContextHeader';
import TermsContent from './TermsContent';

const PaymentCheckout = ({ bookingId, isOpen, onClose, onSuccess, context }) => {
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [paymentOption, setPaymentOption] = useState('deposit'); // 'deposit' or 'full'
  const [paymentOptions, setPaymentOptions] = useState(null); // Discount info from API
  // Cancellation policy modal — opened in-place above the payment overlay so
  // the deposit flow (selected option, in-flight Stripe redirect intent) is
  // preserved. Uses a hand-rolled fixed overlay at z-[90] to sit above the
  // payment modal's z-[80] without requiring changes to the shared Shadcn
  // Dialog primitive. iOS Capacitor WebViews ignore `target="_blank"` for
  // same-origin URLs, so the previous <a href="/terms" target="_blank">
  // silently no-op'd on native.
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isOpen && bookingId) {
      fetchBooking();
      fetchPaymentOptions();
      fetchPlatformSettings();
      setError(''); // Clear errors when opening
      setPaymentOption('deposit'); // Reset to default on open
      
      // Prevent body scroll on mobile
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen, bookingId]);

  const fetchBooking = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBooking(data);
      }
    } catch (err) {
      console.error('Failed to fetch booking:', err);
    }
  };

  const fetchPaymentOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/payment-options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentOptions(data);
      }
    } catch (err) {
      console.error('Failed to fetch payment options:', err);
    }
  };

  const fetchPlatformSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/agreements/service_agreement`);
      if (response.ok) {
        const data = await response.json();
        // Settings are embedded in agreement or separate endpoint
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  // Reset stuck payment attempts
  const handleResetPayment = async () => {
    setResetting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/cancel-payment-attempt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.status === 'paid') {
        // Payment was actually successful!
        if (onSuccess) onSuccess();
        onClose();
        return;
      }
      
      // Refresh booking data
      await fetchBooking();
      setError('');
    } catch (err) {
      console.error('Failed to reset payment:', err);
      setError('Failed to reset payment. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to continue');
      }
      
      // Pass payment_option as query parameter
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/create-checkout?payment_option=${paymentOption}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Get response text first to handle both JSON and non-JSON responses
      const responseText = await response.text();
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        // Response wasn't valid JSON - log for debugging
        console.error('Failed to parse response:', responseText.substring(0, 200));
        throw new Error(response.status === 500 
          ? 'Server error. Please try again or contact support.'
          : `Server error (${response.status}). Please try again.`);
      }
      
      if (!response.ok) {
        // Check if it's an "already paid" case
        if (data.detail && data.detail.includes('already completed')) {
          if (onSuccess) onSuccess();
          onClose();
          return;
        }
        throw new Error(data.detail || `Payment failed (${response.status})`);
      }

      // Log if we reused an existing session
      if (data.reused) {
        console.log('[Payment] Reusing existing active session');
      }

      // Redirect to Stripe Checkout using Capacitor Browser
      if (data.checkout_url) {
        await openExternalUrl(data.checkout_url);
      } else {
        throw new Error('Payment system unavailable. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.message || 'An error occurred while processing payment';
      setError(errorMessage);
      
      // If there's a stuck session error, show reset option
      if (errorMessage.includes('stuck') || errorMessage.includes('pending')) {
        setError(errorMessage + ' Click "Reset" below to clear and retry.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !booking) return null;

  // Determine if this is deposit phase or final payment phase
  const isDepositPhase = !booking.deposit_paid;
  const isFinalPhase = booking.deposit_paid && !booking.final_paid;
  
  // Check if payment window has expired (server-provided)
  const isPaymentWindowExpired = paymentOptions?.payment_window_status === 'expired' || 
                                  paymentOptions?.can_pay_deposit === false;
  
  // Calculate amounts based on selected payment option and discount
  const depositAmount = booking.deposit_amount || 0;
  const finalAmount = booking.final_amount || 0;
  const totalAmount = booking.total_amount || booking.agreed_price || 0;
  
  // Get discount info from payment options API
  const discountEnabled = paymentOptions?.discount_enabled || false;
  const discountPercent = paymentOptions?.discount_percent || 0;
  const discountSavings = paymentOptions?.discount_savings || 0;
  const discountedFullAmount = paymentOptions?.full_amount_discounted || totalAmount;
  
  // Determine the amount to charge based on phase and selection
  let amountToPay = 0;
  let paymentDescription = '';
  
  if (isDepositPhase) {
    if (paymentOption === 'full') {
      // Use discounted amount if discount is enabled
      amountToPay = discountEnabled ? discountedFullAmount : totalAmount;
      paymentDescription = discountEnabled ? `Full Payment (Save ${discountPercent}%)` : 'Full Payment';
    } else {
      amountToPay = depositAmount;
      paymentDescription = `Deposit (${platformSettings?.deposit_percentage || 15}%)`;
    }
  } else if (isFinalPhase) {
    amountToPay = finalAmount;
    paymentDescription = 'Final Payment';
  }
  
  // Stripe minimum charge validation
  const STRIPE_MIN_CHARGE = 0.50;
  const isAmountTooLow = amountToPay < STRIPE_MIN_CHARGE;

  // If payment window expired during deposit phase, show expired UI
  if (isDepositPhase && isPaymentWindowExpired) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4 overflow-y-auto">
        <div className="bg-white rounded-[16px] max-w-md w-full max-h-[90vh] overflow-y-auto my-auto">
          {/* Header */}
          <div className="border-b p-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-700" data-testid="payment-checkout-title">
                Payment Window Expired
              </h2>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="payment-close-btn">
                <X size={24} />
              </button>
            </div>
            {context && (
              <EventContextHeader
                eventId={context.eventId}
                eventName={context.eventName}
                vendorName={context.vendorName}
                vendorService={context.vendorService}
                eventDate={context.eventDate}
                location={context.location}
                onNavigate={onClose}
              />
            )}
          </div>

          {/* Expired Content */}
          <div className="p-6 space-y-4">
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="text-gray-500 mr-3 mt-0.5 flex-shrink-0" size={24} />
                <div>
                  <p className="font-medium text-gray-700 mb-2">This booking date is no longer held</p>
                  <p className="text-sm text-gray-600">
                    The 48-hour payment window has expired. Please message the provider to request a new booking if you&apos;re still interested.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => {
                  onClose();
                  // Note: handleOpenMessaging would need to be passed as a prop to open messaging
                }}
                className="w-full bg-[#C6A96B] text-white py-3 rounded-full hover:bg-[#B89655] font-medium flex items-center justify-center"
              >
                <MessageSquare className="mr-2" size={20} />
                Message Provider
              </button>
              <button
                onClick={onClose}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4 overflow-y-auto">
      <div className="bg-white rounded-[16px] max-w-md w-full max-h-[90vh] overflow-y-auto my-auto">
        {/* Header */}
        <div className="border-b p-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold" data-testid="payment-checkout-title">
              {isDepositPhase ? 'Secure Your Booking' : 'Final Payment'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-testid="payment-close-btn">
              <X size={24} />
            </button>
          </div>
          {context && (
            <EventContextHeader
              eventId={context.eventId}
              eventName={context.eventName}
              vendorName={context.vendorName}
              vendorService={context.vendorService}
              eventDate={context.eventDate}
              location={context.location}
              onNavigate={onClose}
            />
          )}
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Minimum charge warning */}
          {isAmountTooLow && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="text-red-600 mr-2 mt-0.5 flex-shrink-0" size={20} />
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-1">Payment Amount Too Low</p>
                  <p>
                    This amount (${amountToPay.toFixed(2)}) is below Stripe&apos;s minimum charge of $0.50. 
                    Please contact the service provider to adjust the total price.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Payment Options - Only show in deposit phase */}
          {isDepositPhase && (
            <div className="space-y-3" data-testid="payment-options">
              <p className="text-sm font-medium text-gray-700">Choose your payment option:</p>
              
              {/* Option 1: Pay Deposit */}
              <button
                onClick={() => setPaymentOption('deposit')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                  paymentOption === 'deposit' 
                    ? 'border-[#C6A96B] bg-[#FAF8F3]' 
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                data-testid="payment-option-deposit"
              >
                <div className="flex items-start gap-3">
                  {paymentOption === 'deposit' ? (
                    <CircleDot className="h-5 w-5 text-[#C6A96B] mt-0.5 flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Pay Deposit Only</span>
                      <span className="font-bold text-[#C6A96B]">${depositAmount.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {platformSettings?.deposit_percentage || 15}% now, remaining ${finalAmount.toFixed(2)} due later
                    </p>
                  </div>
                </div>
              </button>
              
              {/* Option 2: Pay Full Amount (with discount if enabled) */}
              <button
                onClick={() => setPaymentOption('full')}
                className={`w-full p-4 rounded-lg border-2 text-left transition-all relative ${
                  paymentOption === 'full' 
                    ? 'border-[#C6A96B] bg-[#FAF8F3]' 
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                } ${discountEnabled ? 'ring-2 ring-amber-200' : ''}`}
                data-testid="payment-option-full"
              >
                {/* Discount Badge */}
                {discountEnabled && discountPercent > 0 && (
                  <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Save {discountPercent}%
                  </div>
                )}
                <div className="flex items-start gap-3">
                  {paymentOption === 'full' ? (
                    <CircleDot className="h-5 w-5 text-[#C6A96B] mt-0.5 flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">
                        Pay in Full
                        {discountEnabled && discountPercent > 0 && (
                          <span className="text-amber-600 ml-1">- Save {discountPercent}%</span>
                        )}
                      </span>
                      <div className="text-right">
                        {discountEnabled && discountSavings > 0 ? (
                          <>
                            <span className="font-bold text-[#C6A96B]">${discountedFullAmount.toFixed(2)}</span>
                            <span className="text-xs text-gray-400 line-through ml-1">${totalAmount.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="font-bold text-[#C6A96B]">${totalAmount.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Complete payment now — no future payments needed
                    </p>
                    {discountEnabled && discountSavings > 0 && (
                      <p className="text-xs text-amber-600 font-medium mt-1">
                        You save ${discountSavings.toFixed(2)}!
                      </p>
                    )}
                  </div>
                </div>
              </button>
            </div>
          )}
          
          {/* Payment Info */}
          <div className="bg-[#FAF8F3] border border-[#E6E6E0] rounded-[14px] p-4">
            <div className="flex items-start">
              <AlertCircle className="text-[#C6A96B] mr-2 mt-1 flex-shrink-0" size={20} />
              <div className="text-sm text-[#3D3D3D]">
                {isDepositPhase && paymentOption === 'deposit' ? (
                  <>
                    <p className="font-medium mb-2">How It Works</p>
                    <ul className="space-y-1 ml-4 list-disc">
                      <li>Pay {platformSettings?.deposit_percentage || 15}% deposit to secure your booking</li>
                      <li>Service provider can then begin work</li>
                      <li>Pay remaining balance after service is completed</li>
                      <li>Your payment is securely held until the service is complete</li>
                      <li>All payments are securely processed through Stripe</li>
                    </ul>
                  </>
                ) : isDepositPhase && paymentOption === 'full' ? (
                  <>
                    <p className="font-medium mb-2">Pay in Full</p>
                    <ul className="space-y-1 ml-4 list-disc">
                      <li>Complete your payment in one transaction</li>
                      <li>No future payments required</li>
                      {discountEnabled && discountSavings > 0 && (
                        <li className="text-amber-700 font-medium">Save ${discountSavings.toFixed(2)} with this option!</li>
                      )}
                      <li>Service provider can begin work immediately</li>
                      <li>Your payment is securely held until the service is complete</li>
                      <li>All payments are securely processed through Stripe</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="font-medium mb-2">Final Payment</p>
                    <p>This is the remaining balance for your booking. Your payment is securely held and released to the service provider once you confirm completion.</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Amount Summary */}
          <div className="border border-gray-200 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Project Price:</span>
              <span className="font-medium">${totalAmount.toFixed(2)}</span>
            </div>
            {isDepositPhase && (
              <>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Deposit ({platformSettings?.deposit_percentage || 15}%):</span>
                  <span>${depositAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Remaining Balance:</span>
                  <span>${finalAmount.toFixed(2)}</span>
                </div>
              </>
            )}
            {/* Show discount line when full payment with discount is selected */}
            {isDepositPhase && paymentOption === 'full' && discountEnabled && discountSavings > 0 && (
              <div className="flex justify-between text-sm text-amber-600 font-medium bg-amber-50 -mx-4 px-4 py-1">
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  Pay-in-Full Discount ({discountPercent}%):
                </span>
                <span>-${discountSavings.toFixed(2)}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between">
              <span className="font-semibold">Amount Due Now:</span>
              <span className="font-bold text-[#C6A96B] text-lg" data-testid="amount-due-now">
                ${amountToPay.toFixed(2)}
              </span>
            </div>
            {isDepositPhase && paymentOption === 'full' && (
              <p className="text-xs text-[#C6A96B] text-center font-medium">
                ✓ No future payments required
                {discountEnabled && discountSavings > 0 && ` • You save $${discountSavings.toFixed(2)}!`}
              </p>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              <div className="flex items-start">
                <AlertCircle size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p>{error}</p>
                  {/* Show reset button for stuck payment issues */}
                  <button
                    onClick={handleResetPayment}
                    disabled={resetting}
                    className="mt-2 text-red-600 hover:text-red-800 underline text-xs font-medium"
                  >
                    {resetting ? 'Resetting...' : 'Having trouble? Click here to reset and try again'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Button */}
          <button
            onClick={handlePayment}
            disabled={loading || isAmountTooLow}
            className="w-full bg-[#C6A96B] text-white py-3 rounded-full hover:bg-[#B89655] font-medium flex items-center justify-center disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="payment-submit-btn"
          >
            {loading ? (
              <>
                <Loader className="animate-spin mr-2" size={20} />
                Processing...
              </>
            ) : isAmountTooLow ? (
              <>
                <AlertCircle className="mr-2" size={20} />
                Amount Below Minimum
              </>
            ) : (
              <>
                <CreditCard className="mr-2" size={20} />
                {isDepositPhase && paymentOption === 'full' 
                  ? `Pay $${amountToPay.toFixed(2)} Now` 
                  : `Pay ${paymentDescription}`
                }
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 text-center">
            Payments are processed securely by Stripe. You will be redirected to complete your payment.
          </p>

          {/* App Store Payment Readiness P1 (2026-06-01): inline refund/
              cancellation disclosure pre-payment. Full policy text lives in
              the Service Agreement (already accepted earlier in this flow)
              and on /terms. This single-sentence reminder satisfies App
              Store review guideline 3.1 transparency expectations without
              duplicating the long-form text. */}
          {isDepositPhase && (
            <p className="text-xs text-gray-500 text-center" data-testid="payment-refund-disclosure">
              Full refund if you cancel within 48 hours of paying.{' '}
              <button
                type="button"
                onClick={() => setShowTermsDialog(true)}
                className="text-[#C6A96B] underline bg-transparent border-0 p-0 cursor-pointer"
                data-testid="payment-refund-policy-link"
              >
                View cancellation policy
              </button>.
            </p>
          )}
        </div>
      </div>

      {/* Cancellation policy modal — elevated above the payment overlay
          (z-[90] > z-[80]) so the deposit flow stays mounted underneath.
          Dismissing returns the user to the payment screen with all
          selections intact. */}
      {showTermsDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4 overflow-y-auto"
          data-testid="payment-terms-dialog"
          onClick={(e) => {
            // Close on backdrop click only (not when clicking inner content)
            if (e.target === e.currentTarget) setShowTermsDialog(false);
          }}
        >
          <div className="bg-white rounded-[16px] max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden my-auto">
            <div className="border-b p-6 flex justify-between items-start">
              <div>
                <h2 className="text-lg font-bold" data-testid="payment-terms-dialog-title">
                  Terms of Service
                </h2>
                <p className="text-xs text-gray-400 mt-1">Last updated: April 2026</p>
              </div>
              <button
                onClick={() => setShowTermsDialog(false)}
                className="text-gray-500 hover:text-gray-700"
                data-testid="payment-terms-dialog-close"
                aria-label="Close cancellation policy"
              >
                <X size={24} />
              </button>
            </div>
            <div
              className="flex-1 overflow-y-auto px-6 py-5"
              data-testid="payment-terms-dialog-body"
            >
              <TermsContent />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCheckout;